/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : CustomAutoCompleteWithAPIV2.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */

import {
  Autocomplete,
  CircularProgress,
  InputAdornment,
  TextField,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import SearchIcon from "@mui/icons-material/Search";

import { useDispatch } from "react-redux";
import { HandleApiActions } from "../../../../redux/actions";
import { componentStyle } from "../../../../config/appTheme";
import CustomToolTip from "../../../infoButton/CustomToolTip";

function CustomAutoCompleteWithAPIV2({
  apiAction,
  params = {},
  searchKey,
  displayKey,
  valueKey,
  getDataOnSearch,
  firstItemDefaultSelect = false,
  getSelectedItem,
  renderOptionTemplate,
  valueObj,
  size,
  type,
  enableDarkMode = false,
  variant,
  placeholder,
  valueError,
  disabled,
  txtFldStyle = {},
  toolTipLength = Infinity,
  skipApiCall = false,
  skipInitialSearch = true, // NEW: Skip initial API call on mount
}) {
  const dispatch = useDispatch();
  const [searchText, setSearchText] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isUserTyping, setIsUserTyping] = useState(false);
  const isFirstRun = useRef(true);
  const prevParamsRef = useRef(params);
  const didMountRef = useRef(false);
  const didInitialSearchRef = useRef(false);
  const searchTextRef = useRef(searchText);

  useEffect(() => {
    setSearchText("");
  }, []);

  useEffect(() => {
    searchTextRef.current = searchText;
  }, [searchText]);

  const handleSearch = (query) => {
    if (skipApiCall) return;

    setLoading(true);
    console.log("--handle search--", params);
    dispatch(
      HandleApiActions({
        ...apiAction,
        params: {
          ...params,
          ...(query !== "" && { [searchKey]: query }),
          ignore_app_loading_spinner: true,
        },
        show_toast: false,
        ignore_app_loading_spinner: true,
      }),
    )
      .then((response) => {
        if (response?.error) {
          setOptions([]);
          getSelectedItem(undefined);
          getDataOnSearch?.([], response);
        } else {
          setOptions(response?.data || []);
          getDataOnSearch?.(response?.data, response);
          if (firstItemDefaultSelect && isFirstRun.current) {
            getSelectedItem(response?.data[0]);
          }
          isFirstRun.current = false;
        }
      })
      .catch((err) => {
        console.error("API Search Error:", err);
        setOptions([]);
        getDataOnSearch?.([]);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    const timer = setTimeout(() => handleSearch(searchText), 500);
    return () => clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    const paramsChanged =
      JSON.stringify(prevParamsRef.current) !== JSON.stringify(params);

    if (didInitialSearchRef.current) {
      if (paramsChanged) {
        handleSearch(searchTextRef.current);
      }
    } else {
      if (!skipInitialSearch) {
        handleSearch("");
      }
      didInitialSearchRef.current = true;
    }

    prevParamsRef.current = params;
  }, [params, skipInitialSearch]);

  useEffect(() => {
    if (!isUserTyping) {
      setInputValue(valueObj?.[displayKey] || searchText);
    }
  }, [valueObj, displayKey, isUserTyping]);

  useEffect(() => {
    console.log("--valueObj--", valueObj);
  }, [valueObj]);
  return (
    <Autocomplete
      fullWidth
      autoHighlight
      openOnFocus
      freeSolo
      clearOnBlur={false}
      selectOnFocus={false}
      disabled={disabled}
      options={options}
      filterOptions={(x) => x}
      getOptionLabel={(option) => option?.[displayKey] || ""}
      sx={enableDarkMode ? style.autoCompleteDark : style.autoCompleteLight}
      loading={loading}
      value={valueObj ?? null}
      inputValue={inputValue}
      isOptionEqualToValue={(option, value) => {
        console.log("---isOptionEqualToValue---", option, value);
        return option?.[valueKey] === value?.[valueKey];
      }}
      onInputChange={(e, value, reason) => {
        if (reason === "input") {
          setInputValue(value);
          setIsUserTyping(true);
          setSearchText(value);
          return;
        }

        if (reason === "clear") {
          setIsUserTyping(true);
          setInputValue("");
          setSearchText("");
          return;
        }

        if (reason === "reset" && value) {
          setInputValue(value);
        }

        setIsUserTyping(false);
      }}
      onChange={(e, value, reason) => {
        if (reason === "selectOption") {
          setIsUserTyping(false);
          getSelectedItem?.(value);
          setInputValue(value?.[displayKey] || searchText);
        } else if (reason === "createOption") {
          setIsUserTyping(false);
          setInputValue(typeof value === "string" ? value : searchText);
        } else if (reason === "clear") {
          setIsUserTyping(true);
          setInputValue("");
          setSearchText("");
        }
      }}
      onBlur={() => {
        setIsUserTyping(false);
      }}
      renderOption={(props, option) =>
        renderOptionTemplate ? (
          renderOptionTemplate(props, option)
        ) : (
          <li
            {...props}
            style={{
              backgroundColor: "white",
              borderRadius: 0,
            }}
          >
            <CustomToolTip
              length={toolTipLength}
              props={{
                onMouseEnter: (e) => {
                  e.currentTarget.closest("li").style.backgroundColor =
                    "#e0e0e0";
                },
                onMouseLeave: (e) => {
                  e.currentTarget.closest("li").style.backgroundColor = "white";
                },
              }}
            >
              {option?.[displayKey]}
            </CustomToolTip>
          </li>
        )
      }
      renderInput={(params) => (
        <TextField
          {...params}
          size={size}
          type={type}
          variant={variant}
          placeholder={placeholder}
          sx={{
            ...(enableDarkMode ? style.textFieldDark : style.textFieldLight),
          }}
          error={Boolean(valueError)}
          helperText={valueError}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: (
              <>
                {loading && (
                  <CircularProgress size={18} sx={{ color: "black" }} />
                )}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      slotProps={{
        paper: {
          sx: {
            borderRadius: 0,
            padding: 0,
            margin: 0,
          },
        },
        listbox: {
          sx: {
            padding: 0,
            margin: 0,
            width: "100%",
            overflowY: "auto",
            maxHeight: 240,
            ...componentStyle.scroll,
          },
        },
      }}
    />
  );
}

CustomAutoCompleteWithAPIV2.propTypes = {
  apiAction: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  params: PropTypes.object,
  searchKey: PropTypes.string,
  displayKey: PropTypes.string,
  valueKey: PropTypes.string,
  getDataOnSearch: PropTypes.func,
  firstItemDefaultSelect: PropTypes.bool,
  getSelectedItem: PropTypes.func,
  renderOptionTemplate: PropTypes.func,
  valueObj: PropTypes.any,
  size: PropTypes.string,
  type: PropTypes.string,
  enableDarkMode: PropTypes.bool,
  variant: PropTypes.string,
  placeholder: PropTypes.string,
  valueError: PropTypes.any,
  disabled: PropTypes.bool,
  txtFldStyle: PropTypes.object,
  toolTipLength: PropTypes.number,
  skipApiCall: PropTypes.bool,
  skipInitialSearch: PropTypes.bool,
};

CustomAutoCompleteWithAPIV2.defaultProps = {
  apiAction: null,
  params: {},
  searchKey: "",
  displayKey: "",
  valueKey: "",
  getDataOnSearch: () => {},
  firstItemDefaultSelect: false,
  getSelectedItem: () => {},
  renderOptionTemplate: null,
  valueObj: null,
  size: "small",
  type: "text",
  enableDarkMode: false,
  variant: "outlined",
  placeholder: "",
  valueError: null,
  disabled: false,
  txtFldStyle: {},
  toolTipLength: Infinity,
  skipApiCall: false,
  skipInitialSearch: true,
};

export default CustomAutoCompleteWithAPIV2;

const style = {
  autoCompleteLight: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 0,
    },
    "& .MuiInputBase-input": {
      fontSize: 14,
    },
  },

  autoCompleteDark: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 0,
      backgroundColor: "transparent !important",
      color: "white",

      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "white",
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: "#D0D0CE",
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "white !important",
      },

      "& .MuiSvgIcon-root": {
        color: "white",
      },
    },

    "& .MuiInputBase-input": {
      fontSize: 14,
      color: "white",
    },

    "& .MuiInputBase-input::placeholder": {
      color: "white",
      opacity: 0.7,
    },
  },
  listBoxLight: {
    sx: {
      maxHeight: 240,
      overflowY: "auto",

      padding: 0,
      ...componentStyle.scroll,
    },
  },

  textFieldLight: {
    "& .MuiOutlinedInput-root": {
      height: "32px",
      "& fieldset": { borderColor: "#D0D0CE", borderRadius: "0px" }, // Default
      "&:hover fieldset": { borderColor: "#D0D0CE" }, // Hover
      "&.Mui-focused fieldset": { borderColor: "#000000" }, // Focused
      "&.Mui-disabled fieldset": { borderColor: "#D0D0CE" }, // Disabled
      "&.Mui-error fieldset": { borderColor: "#e53935" }, // Error

      "&:hover .MuiInputBase-input::placeholder": {
        color: "#848587", // dark text color on hover
      },

      "&.Mui-focused .MuiInputBase-input::placeholder": {
        color: "#232323", // solid black when focused
      },
    },

    "& .MuiInputLabel-root": {
      color: "#000",
      fontSize: "0.9rem",
      "&.Mui-error": { color: "#e53935" },
      "&.Mui-disabled": { color: "#bdbdbd" },
    },
    "& .MuiInputBase-input::placeholder": {
      color: "#999",
      opacity: 1,
    },
    "& .MuiFormHelperText-root.Mui-error": {
      color: "#e53935",
      fontSize: "0.8rem",
      marginLeft: 0,
      marginTop: "4px",
    },
  },
  textFieldDark: {
    "& .MuiOutlinedInput-root": {
      height: "28px",
      "& fieldset": {
        borderColor: "#D0D0CE",
        borderRadius: "0px",
        color: "#D0D0CE",
      }, // Default
      "&:hover fieldset": { borderColor: "#D0D0CE", color: "#D0D0CE" }, // Hover
      "&.Mui-focused fieldset": { borderColor: "#ffffff", color: "#fff" }, // Focused
      "&.Mui-disabled fieldset": { borderColor: "#D0D0CE", color: "#D0D0CE" }, // Disabled
      "&.Mui-error fieldset": { borderColor: "#e53935" }, // Error

      "&:hover .MuiInputBase-input::placeholder": {
        color: "#848587", // dark text color on hover
      },

      "& .MuiInputBase-input": {
        color: "#fff !important", // 🔥 selected text color ALWAYS white
      },
      "& .MuiOutlinedInput-root.Mui-focused .MuiInputBase-input": {
        color: "#fff !important", // 🔥 text stays white when focused
      },
      "& .MuiOutlinedInput-root:hover .MuiInputBase-input": {
        color: "#fff !important", // 🔥 white on hover too
      },

      "&.Mui-focused .MuiInputBase-input::placeholder": {
        color: "#232323", // solid black when focused
      },
    },

    "& .MuiInputLabel-root": {
      color: "#fff",
      fontSize: "0.9rem",
      "&.Mui-error": { color: "#e53935" },
      "&.Mui-disabled": { color: "#bdbdbd" },
    },
    "& .MuiInputBase-input::placeholder": {
      color: "#999",
      opacity: 1,
    },
    "& .MuiFormHelperText-root.Mui-error": {
      color: "#e53935",
      fontSize: "0.8rem",
      marginLeft: 0,
      marginTop: "4px",
    },
  },
};
